import { motion } from "motion/react"
import { useEffect, useRef, useState } from "react"
import { CampaignStory } from "../../types"
import { useBreakpoint } from "@/lib/use-breakpoint"
import posthog from "posthog-js"
import { cn, getTrueDate } from "@/lib/utils"
import { useGetCreateStoryImage, useGetStoryStatus } from "../../api/campaign"
import { TextShimmer } from "@/shared/ui/text-shimmer.tsx"
import { IconChecks } from "@tabler/icons-react"
import { CheckCircle, Loader2 } from "lucide-react"
import { useAtomValue } from "jotai"
import { themeAtom } from "@/features/app-layout/atoms/theme-atoms"
// import DiscardDialog from "./DiscardDialog"

type Props = {
  data: CampaignStory,
  id: number,
  type: "create" | "pending"
  onThemeSelect: (id: number, nav: string, story_id: number) => void,
  event_id: number,
  image_id?: number,
  image_url?: string,
  onReasoning: (name: string, why: string) => void
}

const CampaignCard = ({ data, onThemeSelect, onReasoning, event_id, image_id, image_url }: Props) => {

  const hasImageGenerated = useRef(false)

  const theme = useAtomValue(themeAtom)

  const [isLoading, setIsLoading] = useState(true)

  const { storyCreationStatus } = useGetStoryStatus(data.story_id, event_id ,data.status !== "Approved", image_id)

  const { generateImage } = useGetCreateStoryImage(() => {
    setIsLoading(false)
    hasImageGenerated.current = true
  })

  useEffect(() => {
    if (storyCreationStatus) {
      if (!storyCreationStatus.image && theme.isImageClient && !hasImageGenerated.current) {
        generateImage({ eventId: event_id, storyId: data.story_id })
      } else {
        setIsLoading(false)
      }
    }
  }, [storyCreationStatus])

  const [viewed, _] = useState(false)

  const [isHovered, setIsHovered] = useState(false)

  const size = (window.innerWidth < 1280 && window.innerWidth > 1024) ? 40 : 100

  const breakpoint = useBreakpoint();

  const getWidth = () => {
    if (breakpoint === "mobile") {
      return isHovered ? "4rem" : "3rem";
    }
    if (breakpoint === "tablet") {
      return isHovered ? "5rem" : "4rem";
    }
    return isHovered ? "3rem" : "2rem"; // desktop
  };

  return (
    <div className={cn("p-[2px] overflow-hidden bg-gradient-to-tr bg-[size:400%_400%] animate-wave rounded-3xl",
      (data.status === "Pending") ? "from-purple-300 via-pink-400 to-fuchsia-600" : "from-purple-400 via-yellow-300 to-pink-600"
    )}>
      <motion.div
        onClick={() => {
          if (!isLoading && data.status !== "Approved") {
            onThemeSelect(event_id, "result", data.story_id)
            posthog.capture('story viewed', { event_id: data.story_id })
          }
        }}
        className="flex-1 h-32 rounded-3xl p-2 text-white shadow-xl cursor-pointer relative bg-white"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="absolute inset-4 flex flex-col justify-between">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <motion.div
                className="flex-shrink-0 overflow-hidden w-20"
                animate={{
                  fontSize: getWidth(),
                }}
                transition={{ duration: 0.6, ease: "easeInOut" }}
              >
                {image_url 
                ? <img src={image_url} className="size-full object-cover rounded-xl p-1" />
                 : <div
                    className="w-full h-full object-cover rounded-lg flex items-center justify-center text-purple-700 font-semibold font-montserrat shadow-lg"
                  >{data.sequence_order}</div>
                }
              </motion.div>
              <div className="flex-1 min-w-0">
                <motion.h2
                  initial={false}
                  animate={{
                    fontSize: isHovered ? "1.3rem" : "1rem",
                  }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                  className={cn("text-base lg:text-lg font-bold lg:leading-[1.2rem] tracking-wide",
                    viewed ? "text-pink-500" : "text-purple-600"
                  )}>{data.story_name}</motion.h2>
                {!isLoading ?
                  <motion.p
                    className="text-[0.7rem] lg:text-xs text-gray-500 line-clamp-2"
                    animate={{
                      opacity: isHovered ? 0 : 1,
                      y: isHovered ? -10 : 0,
                    }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    {data.story_description}
                  </motion.p> : data.status === "Pending" ? (<TextShimmer className='font-poppins text-sm tracking-wider font-medium' duration={5}>
                    Generating story...
                  </TextShimmer>) : <></>
                }
                {data.status === "Approved" && (
                  <motion.p
                    className="text-[0.7rem] lg:text-xs text-gray-500"
                    animate={{
                      opacity: isHovered ? 0 : 1,
                      y: isHovered ? -10 : 0,
                    }}
                    transition={{ duration: 0.4, ease: "easeInOut" }}
                  >
                    {data.story_description.slice(0, size)}...
                  </motion.p>
                )}
              </div>
            </div>
            <div className={cn("rounded-xl w-9 sm:w-12 flex items-center justify-center gap-0 flex-col aspect-square shadow-lg ml-2 border border-purple-300",
              viewed ? "bg-pink-500 text-white border-pink-500" : "bg-white text-indigo-600"
            )}>

              <span className="text-xs sm:text-sm font-bold h-4">{getTrueDate(data.story_date, "MMM")}</span>
              <span className="text-base sm:text-xl font-bold ">{getTrueDate(data.story_date, "d")}</span>
            </div>
          </div>

          {!isLoading
            ? <motion.button
              initial={{ opacity: 0, y: 8 }}
              animate={{
                opacity: isHovered ? 0 : 1,
                y: isHovered ? 8 : 0,
              }}
              transition={{
                duration: 0.4,
                ease: "easeOut",
                delay: isHovered ? 0.1 : 0,
              }}
              className="flex justify-end absolute bottom-0 right-2 text-sm underline text-black underline-offset-4 cursor-pointer"
            >
              <IconChecks />
            </motion.button>
            : ( data.status !== "Approved"  ) ? <motion.button
              initial={{ opacity: 1, y: 8 }}
              animate={{
                opacity: isHovered ? 0 : 1,
                y: isHovered ? 8 : 0,
              }}
              transition={{
                duration: 0.4,
                ease: "easeOut",
                delay: isHovered ? 0 : 0.1,
              }}
              className="flex justify-end absolute bottom-0 right-2 text-sm underline text-black underline-offset-4 cursor-pointer"
            >
              <Loader2 className="animate-spin" />
            </motion.button>
              : <></>
          }
          {data.status === "Approved" && (
            <motion.button
              className="flex justify-end absolute bottom-0 right-2 text-sm underline text-black underline-offset-4 cursor-pointer"
            >
              <CheckCircle className="text-green-500" size={16} />
            </motion.button>
          )}

          {data.status !== "Approved" && <motion.button
            onClick={(e) => {
              e.stopPropagation()
              onReasoning(data.story_name, data.story_description)
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={{
              opacity: isHovered ? 1 : 0,
              y: isHovered ? 0 : 8,
            }}
            transition={{
              duration: 0.4,
              ease: "easeOut",
              delay: isHovered ? 0.1 : 0,
            }}
            className="flex justify-end absolute bottom-0 right-2 text-sm underline text-black underline-offset-4 cursor-pointer"
          >
            Why this idea?
          </motion.button> }
        </div>
      </motion.div>
    </div>
  )
}

export default CampaignCard
